import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";
import {
  PageContainer,
  PageHeader,
  Card,
  CardHeader,
  Table,
  Thead,
  Th,
  Td,
  TRow,
  EmptyState,
  Button,
  FormGroup,
  Input,
  Select,
  Badge,
  TabGroup,
  StatCard,
} from "../ui";

// ============================================================
// TYPES
// ============================================================

interface Employee {
  id: string;
  name: string;
  phone: string;
  hourly_rate: number;
  pay_type: "W2" | "1099";
  hire_date: string;
  status: "active" | "inactive";
  hours_this_week?: number;
  role?: string;
}

interface Shift {
  id: string;
  employee_id: string;
  employee_name?: string;
  scheduled_start: string;
  scheduled_end: string;
  role?: string;
  notes?: string;
  status?: string;
  swap_claimed_by?: string | null;
  claimed_first_name?: string | null;
  claimed_last_name?: string | null;
  published_at?: string | null;
}

interface ClockEntry {
  employee_id: string;
  employee_name: string;
  punch_in_at: string;
  punch_out_at?: string;
  elapsed_minutes?: number;
  punch_in_note?: string | null;
}

interface TimeOffRequest {
  id: string;
  employee_id: string;
  employee_name: string;
  start_date: string;
  end_date: string;
  reason: string;
  status: "pending" | "approved" | "denied";
  decided_at?: string;
}

interface PayrollRow {
  employee_id: string;
  employee_name: string;
  regular_hours: number;
  ot_hours: number;
  rate: number;
  regular_pay: number;
  ot_pay: number;
  gross_pay: number;
}

interface PayrollCalc {
  period_start: string;
  period_end: string;
  rows: PayrollRow[];
  total_gross: number;
}

interface PayrollHistory {
  id: string;
  period_start: string;
  period_end: string;
  total_gross: number;
  created_at: string;
}

interface Punch {
  id: string;
  employee_id: string;
  employee_name: string;
  punch_in_at: string;
  punch_out_at: string;
  edit_reason?: string;
}

// ============================================================
// HELPERS
// ============================================================

function getMonday(d: Date): Date {
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const mon = new Date(d);
  mon.setDate(diff);
  mon.setHours(0, 0, 0, 0);
  return mon;
}

function getSunday(d: Date): Date {
  const day = d.getDay();
  const diff = d.getDate() - day;
  const sun = new Date(d);
  sun.setDate(diff);
  sun.setHours(0, 0, 0, 0);
  return sun;
}

function getWeekStart(d: Date): Date {
  return getSunday(d);
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function fmtDate(d: Date): string {
  return d.toISOString().split("T")[0];
}

function fmtShortTime(iso: string): string {
  const d = new Date(iso);
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "p" : "a";
  h = h % 12 || 12;
  return m === 0 ? `${h}${ampm}` : `${h}:${m.toString().padStart(2, "0")}${ampm}`;
}

function fmtElapsed(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return `${h}h ${m}m`;
}

function fmtMoney(n: number): string {
  return "$" + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

const ROLE_COLORS: Record<string, string> = {
  cashier: "#8BB8E830",
  manager: "#F6BE0030",
  stocker: "#2D8A4E30",
  delivery: "#7C3AED30",
  bartender: "#C5303030",
};

function roleColor(role?: string): string {
  if (!role) return "transparent";
  return ROLE_COLORS[role.toLowerCase()] || "#8BB8E815";
}

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// ============================================================
// MODAL
// ============================================================

function Modal({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className="rounded-2xl p-6 w-full max-w-lg mx-4"
        style={{ background: "var(--rbw-bg-card)", border: "1px solid var(--rbw-border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold" style={{ color: "var(--rbw-navy)" }}>
            {title}
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-lg transition-colors"
            style={{ color: "var(--rbw-gray-dark)" }}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ============================================================
// SCHEDULE TAB
// ============================================================

interface WeekStats {
  current: { total_hours: number; est_cost: number; unrated_hours: number; sales_total: number };
  prior_avg: { weeks: number; scheduled_hours: number; est_cost: number; sales_total: number };
}

function ScheduleTab({ employees }: { employees: Employee[] }) {
  const { user } = useAuth();
  const isManager = user?.role === "owner" || user?.role === "manager";
  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [weekStats, setWeekStats] = useState<WeekStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editShift, setEditShift] = useState<Shift | null>(null);

  // Add shift form
  const [formEmpId, setFormEmpId] = useState("");
  const [formDate, setFormDate] = useState("");
  const [formStart, setFormStart] = useState("09:00");
  const [formEnd, setFormEnd] = useState("17:00");
  const [formRole, setFormRole] = useState("");
  const [formNotes, setFormNotes] = useState("");

  const loadShifts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/scheduling/schedule", { params: { week_start: fmtDate(weekStart) } });
      setShifts(res.data.shifts || res.data || []);
    } catch {
      setShifts([]);
    }
    setLoading(false);
  }, [weekStart]);

  useEffect(() => {
    loadShifts();
  }, [loadShifts]);

  useEffect(() => {
    if (!isManager) return;
    let alive = true;
    api
      .get("/scheduling/week-stats", { params: { week_start: fmtDate(weekStart) } })
      .then((res) => {
        if (alive) setWeekStats(res.data);
      })
      .catch(() => {
        if (alive) setWeekStats(null);
      });
    return () => {
      alive = false;
    };
  }, [weekStart]);

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const getShiftsForCell = (empId: string, dayIdx: number) => {
    const day = days[dayIdx].toDateString();
    // Compare in local time — the API returns UTC instants, and evening
    // shifts cross the UTC date boundary.
    return shifts.filter(
      (s) => s.employee_id === empId && new Date(s.scheduled_start).toDateString() === day
    );
  };

  const weekHoursByEmployee = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const s of shifts) {
      const ms = new Date(s.scheduled_end).getTime() - new Date(s.scheduled_start).getTime();
      if (ms > 0) totals[s.employee_id] = (totals[s.employee_id] || 0) + ms / 3600000;
    }
    return totals;
  }, [shifts]);

  // Per-day + week totals, computed live from the loaded shifts so they update
  // the moment a shift is added or edited.
  const dayTotals = useMemo(() => {
    const totals = days.map(() => 0);
    for (const s of shifts) {
      const start = new Date(s.scheduled_start);
      const idx = days.findIndex((d) => d.toDateString() === start.toDateString());
      const ms = new Date(s.scheduled_end).getTime() - start.getTime();
      if (idx >= 0 && ms > 0) totals[idx] += ms / 3600000;
    }
    return totals;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shifts, weekStart]);
  const weekTotalHours = dayTotals.reduce((a, b) => a + b, 0);

  // Drafts are only visible to managers; staff never see them.
  const draftCount = shifts.filter((s) => !s.published_at).length;

  const fmtHours = (h: number) => (Number.isInteger(h) ? `${h}h` : `${h.toFixed(1)}h`);

  const handleAddShift = async () => {
    if (!formEmpId || !formDate || !formStart || !formEnd) return;
    try {
      // Convert local wall-clock time to a real UTC instant; a bare
      // "YYYY-MM-DDTHH:MM" string would be stored as UTC and shift by 4-5 hrs.
      const payload = {
        employee_id: formEmpId,
        scheduled_start: new Date(`${formDate}T${formStart}:00`).toISOString(),
        scheduled_end: new Date(`${formDate}T${formEnd}:00`).toISOString(),
        role: formRole || undefined,
        notes: formNotes || undefined,
      };
      if (editShift) {
        await api.put(`/scheduling/shifts/${editShift.id}`, payload);
      } else {
        await api.post("/scheduling/shifts", payload);
      }
      setShowAdd(false);
      setEditShift(null);
      resetForm();
      loadShifts();
    } catch (err) {
      console.error("Failed to save shift:", err);
    }
  };

  const handleDeleteShift = async (id: string) => {
    try {
      await api.delete(`/scheduling/shifts/${id}`);
      loadShifts();
    } catch (err) {
      console.error("Failed to delete shift:", err);
    }
  };

  const handleCopyWeek = async () => {
    const prevWeek = addDays(weekStart, -7);
    try {
      await api.post("/scheduling/schedule/copy", {
        source_week: fmtDate(prevWeek),
        target_week: fmtDate(weekStart),
      });
      loadShifts();
    } catch (err) {
      console.error("Failed to copy week:", err);
    }
  };

  const handleTextSchedule = async () => {
    const label = `${days[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${days[6].toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
    if (!window.confirm(`Text everyone their shifts for ${label}? Each employee gets one text listing only their own shifts.`)) return;
    try {
      const res = await api.post("/scheduling/schedule/text", { week_start: fmtDate(weekStart) });
      const { texted, no_phone } = res.data;
      window.alert(`Schedule texted to ${texted} employee${texted === 1 ? "" : "s"}.${no_phone ? ` ${no_phone} had no phone on file.` : ""}`);
    } catch {
      window.alert("Could not send schedule texts.");
    }
  };

  const handlePublish = async () => {
    const label = `${days[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${days[6].toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
    if (!window.confirm(`Publish ${draftCount} draft shift${draftCount === 1 ? "" : "s"} for ${label}? Staff will see them, and everyone with new shifts gets a text listing them.`)) return;
    try {
      const res = await api.post("/scheduling/schedule/publish", { week_start: fmtDate(weekStart) });
      const { published, texted, no_phone } = res.data;
      window.alert(`Published ${published} shift${published === 1 ? "" : "s"}. Texted ${texted} employee${texted === 1 ? "" : "s"}.${no_phone ? ` ${no_phone} had no phone on file.` : ""}`);
      loadShifts();
    } catch {
      window.alert("Could not publish the schedule.");
    }
  };

  const resetForm = () => {
    setFormEmpId("");
    setFormDate("");
    setFormStart("09:00");
    setFormEnd("17:00");
    setFormRole("");
    setFormNotes("");
  };

  const handleSwapAction = async (shiftId: string, action: "swap-claim" | "swap-approve" | "swap-deny") => {
    try {
      const path = action === "swap-claim" ? `/scheduling/my/shifts/${shiftId}/swap-claim` : `/scheduling/shifts/${shiftId}/${action}`;
      await api.post(path);
      loadShifts();
    } catch (err) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      alert(axiosErr.response?.data?.error || "Swap action failed");
    }
  };

  const openEditShift = (s: Shift) => {
    if (!isManager) return;
    setEditShift(s);
    setFormEmpId(s.employee_id);
    const start = new Date(s.scheduled_start);
    const end = new Date(s.scheduled_end);
    const two = (n: number) => n.toString().padStart(2, "0");
    setFormDate(`${start.getFullYear()}-${two(start.getMonth() + 1)}-${two(start.getDate())}`);
    setFormStart(`${two(start.getHours())}:${two(start.getMinutes())}`);
    setFormEnd(`${two(end.getHours())}:${two(end.getMinutes())}`);
    setFormRole(s.role || "");
    setFormNotes(s.notes || "");
    setShowAdd(true);
  };

  const navWeek = (dir: number) => {
    setWeekStart((prev) => addDays(prev, dir * 7));
  };

  const goToday = () => setWeekStart(getWeekStart(new Date()));

  // Index of today's column in the visible week (-1 when viewing another week)
  const todayIdx = days.findIndex((d) => d.toDateString() === new Date().toDateString());
  const onCurrentWeek = todayIdx >= 0;

  const activeEmployees = employees.filter((e) => e.status === "active");

  return (
    <div>
      {/* Week nav */}
      <div className="flex items-center gap-4 mb-5">
        <Button variant="ghost" size="sm" onClick={() => navWeek(-1)}>
          &larr;
        </Button>
        <span className="text-sm font-semibold" style={{ color: "var(--rbw-navy)" }}>
          {days[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} &ndash;{" "}
          {days[6].toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
        </span>
        <Button variant="ghost" size="sm" onClick={() => navWeek(1)}>
          &rarr;
        </Button>
        <button
          onClick={goToday}
          disabled={onCurrentWeek}
          className="text-xs font-bold px-3 py-1.5 rounded-lg transition-all active:scale-95 disabled:opacity-40"
          style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
          title="Jump back to the current week"
        >
          Today
        </button>
        {isManager && <div className="ml-auto flex gap-2 items-center">
          {draftCount > 0 && (
            <Button size="sm" onClick={handlePublish}>
              Publish {draftCount} draft{draftCount === 1 ? "" : "s"}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={handleTextSchedule}>
            Text Schedule
          </Button>
          <Button variant="ghost" size="sm" onClick={handleCopyWeek}>
            Copy Last Week
          </Button>
          <Button
            size="sm"
            onClick={() => {
              resetForm();
              setEditShift(null);
              setShowAdd(true);
            }}
          >
            + Add Shift
          </Button>
        </div>}
      </div>

      {/* Week summary: hours, payroll cost, and how they compare to sales */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="rounded-2xl px-4 py-3" style={{ background: "var(--rbw-bg-card)", border: "1px solid var(--rbw-border)" }}>
          <div className="text-xs font-semibold" style={{ color: "var(--rbw-gray-dark)" }}>Scheduled this week</div>
          <div className="text-xl font-bold" style={{ color: "var(--rbw-navy)" }}>{fmtHours(weekTotalHours)}</div>
          {weekStats && weekStats.prior_avg.weeks > 0 && (
            <div className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>
              4-wk avg {fmtHours(weekStats.prior_avg.scheduled_hours)}
              {weekStats.prior_avg.scheduled_hours > 0 && (
                <span style={{ color: weekTotalHours > weekStats.prior_avg.scheduled_hours * 1.1 ? "var(--rbw-danger)" : "var(--rbw-gray-dark)" }}>
                  {" "}({weekTotalHours >= weekStats.prior_avg.scheduled_hours ? "+" : ""}
                  {(((weekTotalHours - weekStats.prior_avg.scheduled_hours) / weekStats.prior_avg.scheduled_hours) * 100).toFixed(0)}%)
                </span>
              )}
            </div>
          )}
        </div>
        {weekStats && (
          <div className="rounded-2xl px-4 py-3" style={{ background: "var(--rbw-bg-card)", border: "1px solid var(--rbw-border)" }}>
            <div className="text-xs font-semibold" style={{ color: "var(--rbw-gray-dark)" }}>Est. payroll (scheduled)</div>
            <div className="text-xl font-bold" style={{ color: "var(--rbw-navy)" }}>{fmtMoney(weekStats.current.est_cost)}</div>
            <div className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>
              {weekStats.prior_avg.weeks > 0 && `4-wk avg ${fmtMoney(weekStats.prior_avg.est_cost)}`}
              {weekStats.current.unrated_hours > 0 && (
                <span title="Hours for employees with no hourly rate set — not in the $ total"> · {fmtHours(weekStats.current.unrated_hours)} unrated</span>
              )}
            </div>
          </div>
        )}
        {weekStats && weekStats.current.sales_total > 0 && (
          <div className="rounded-2xl px-4 py-3" style={{ background: "var(--rbw-bg-card)", border: "1px solid var(--rbw-border)" }}>
            <div className="text-xs font-semibold" style={{ color: "var(--rbw-gray-dark)" }}>Sales (same week) · labor %</div>
            <div className="text-xl font-bold" style={{ color: "var(--rbw-navy)" }}>
              {fmtMoney(weekStats.current.sales_total)}
              <span className="ml-2" style={{ color: "var(--rbw-gold-dark, #B8860B)" }}>
                {((weekStats.current.est_cost / weekStats.current.sales_total) * 100).toFixed(1)}%
              </span>
            </div>
            {weekStats.prior_avg.weeks > 0 && weekStats.prior_avg.sales_total > 0 && (
              <div className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>
                4-wk avg {fmtMoney(weekStats.prior_avg.sales_total)} · {((weekStats.prior_avg.est_cost / weekStats.prior_avg.sales_total) * 100).toFixed(1)}%
              </div>
            )}
          </div>
        )}
        {weekStats && weekStats.current.sales_total === 0 && (
          <div className="rounded-2xl px-4 py-3 flex items-center text-xs" style={{ background: "var(--rbw-bg)", border: "1px dashed var(--rbw-border)", color: "var(--rbw-gray-dark)" }}>
            No sales data for this week yet<br />(mPower import lags until cutover)
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="overflow-x-auto">
        <div
          className="rounded-2xl overflow-hidden"
          style={{
            background: "var(--rbw-bg-card)",
            border: "1px solid var(--rbw-border)",
            boxShadow: "0 1px 3px rgba(51,63,72,0.04), 0 4px 12px rgba(51,63,72,0.03)",
          }}
        >
          <table className="w-full" style={{ minWidth: 800 }}>
            <thead>
              <tr style={{ background: "var(--rbw-navy)" }}>
                <th
                  className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider"
                  style={{ color: "var(--rbw-gray)", width: 160 }}
                >
                  Employee
                </th>
                {days.map((d, i) => (
                  <th
                    key={i}
                    className="text-center px-2 py-3 text-xs font-semibold uppercase tracking-wider"
                    style={
                      i === todayIdx
                        ? { color: "var(--rbw-navy)", background: "var(--rbw-gold)" }
                        : { color: "var(--rbw-gray)" }
                    }
                  >
                    {DAY_LABELS[i]}
                    <br />
                    <span className="font-normal text-[10px]">{d.toLocaleDateString("en-US", { month: "numeric", day: "numeric" })}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
                    Loading...
                  </td>
                </tr>
              ) : activeEmployees.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <EmptyState message="No employees yet" detail="Add employees in the Employees tab" />
                  </td>
                </tr>
              ) : (
                activeEmployees.map((emp, idx) => (
                  <tr
                    key={emp.id}
                    style={{
                      borderBottom: "1px solid var(--rbw-border)",
                      background: idx % 2 === 0 ? "transparent" : "var(--rbw-bg)",
                    }}
                  >
                    <td className="px-4 py-3 text-sm font-semibold" style={{ color: "var(--rbw-navy)" }}>
                      {emp.name}
                      {weekHoursByEmployee[emp.id] ? (
                        <span
                          className="ml-2 text-xs font-normal rounded-full px-2 py-0.5"
                          style={{ background: "var(--rbw-bg)", color: "var(--rbw-gray-dark)", border: "1px solid var(--rbw-border)" }}
                          title="Scheduled hours this week"
                        >
                          {fmtHours(weekHoursByEmployee[emp.id])}
                        </span>
                      ) : null}
                    </td>
                    {days.map((_, dayIdx) => {
                      const cellShifts = getShiftsForCell(emp.id, dayIdx);
                      return (
                        <td
                          key={dayIdx}
                          className="px-2 py-2 text-center"
                          style={dayIdx === todayIdx ? { background: "#F6BE0022" } : undefined}
                        >
                          {cellShifts.map((s) => (
                            <div
                              key={s.id}
                              className="text-xs font-medium rounded-lg px-2 py-1 mb-1 cursor-pointer transition-all hover:opacity-80"
                              style={
                                s.published_at
                                  ? { background: roleColor(s.role), color: "var(--rbw-navy)" }
                                  : { background: "var(--rbw-bg)", color: "var(--rbw-gray-dark)", border: "1.5px dashed var(--rbw-gray)" }
                              }
                              onClick={() => openEditShift(s)}
                              title={`${s.published_at ? "" : "DRAFT (staff can't see it yet) | "}${s.role || "Shift"} | Click to edit`}
                            >
                              {!s.published_at && (
                                <div className="text-[9px] font-bold uppercase">draft</div>
                              )}
                              {fmtShortTime(s.scheduled_start)}-{fmtShortTime(s.scheduled_end)}
                              {s.role && (
                                <div className="text-[10px] opacity-60">{s.role}</div>
                              )}
                              {s.status === "swap_requested" && (
                                <div className="mt-0.5">
                                  <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full" style={{ background: "#FEF3C7", color: "#92400E" }}>
                                    {s.claimed_first_name ? `cover: ${s.claimed_first_name}` : "needs cover"}
                                  </span>
                                  {isManager && s.swap_claimed_by && (
                                    <span className="block mt-0.5">
                                      <button className="text-[10px] font-bold underline mr-2" style={{ color: "#16a34a" }}
                                        onClick={(e) => { e.stopPropagation(); handleSwapAction(s.id, "swap-approve"); }}>approve</button>
                                      <button className="text-[10px] font-bold underline" style={{ color: "#dc2626" }}
                                        onClick={(e) => { e.stopPropagation(); handleSwapAction(s.id, "swap-deny"); }}>deny</button>
                                    </span>
                                  )}
                                  {!isManager && !s.swap_claimed_by && (
                                    <button className="block text-[10px] font-bold underline mt-0.5" style={{ color: "var(--rbw-blue)" }}
                                      onClick={(e) => { e.stopPropagation(); handleSwapAction(s.id, "swap-claim"); }}>I can cover this</button>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
            {!loading && activeEmployees.length > 0 && (
              <tfoot>
                <tr style={{ background: "var(--rbw-bg)", borderTop: "2px solid var(--rbw-border)" }}>
                  <td className="px-4 py-2 text-sm font-bold" style={{ color: "var(--rbw-navy)" }}>
                    Total {fmtHours(weekTotalHours)}
                  </td>
                  {dayTotals.map((h, i) => (
                    <td key={i} className="px-2 py-2 text-center text-sm font-bold" style={{ color: h > 0 ? "var(--rbw-navy)" : "var(--rbw-gray)" }}>
                      {h > 0 ? fmtHours(Math.round(h * 100) / 100) : "—"}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Add/Edit Shift Modal */}
      <Modal
        open={showAdd}
        onClose={() => {
          setShowAdd(false);
          setEditShift(null);
          resetForm();
        }}
        title={editShift ? "Edit Shift" : "Add Shift"}
      >
        <div className="grid grid-cols-2 gap-4">
          <FormGroup label="Employee" span={2}>
            <Select value={formEmpId} onChange={setFormEmpId}>
              <option value="">Select employee...</option>
              {activeEmployees.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup label="Date">
            <Input type="date" value={formDate} onChange={setFormDate} />
          </FormGroup>
          <FormGroup label="Role">
            <Select value={formRole} onChange={setFormRole}>
              <option value="">No role</option>
              <option value="cashier">Cashier</option>
              <option value="manager">Manager</option>
              <option value="stocker">Stocker</option>
              <option value="delivery">Delivery</option>
              <option value="bartender">Bartender</option>
            </Select>
          </FormGroup>
          <FormGroup label="Start Time">
            <Input type="time" value={formStart} onChange={setFormStart} />
          </FormGroup>
          <FormGroup label="End Time">
            <Input type="time" value={formEnd} onChange={setFormEnd} />
          </FormGroup>
          <FormGroup label="Notes" span={2}>
            <Input value={formNotes} onChange={setFormNotes} placeholder="Optional notes" />
          </FormGroup>
        </div>
        <div className="flex gap-3 mt-6">
          <Button onClick={handleAddShift}>{editShift ? "Save Changes" : "Add Shift"}</Button>
          {editShift && (
            <Button
              variant="danger"
              onClick={() => {
                handleDeleteShift(editShift.id);
                setShowAdd(false);
                setEditShift(null);
                resetForm();
              }}
            >
              Delete Shift
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={() => {
              setShowAdd(false);
              setEditShift(null);
              resetForm();
            }}
          >
            Cancel
          </Button>
        </div>
      </Modal>
    </div>
  );
}

// ============================================================
// TIMECARD EDITOR (owner/manager) — fix missed or wrong punches
// ============================================================

interface PunchRow {
  id: string;
  punch_in_at: string;
  punch_out_at: string | null;
  hours: number | null;
  edited_by: string | null;
  edit_reason: string | null;
  punch_in_note?: string | null;
  punch_out_note?: string | null;
}

function toLocalInputValue(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function sundayOf(d: Date): string {
  const s = new Date(d);
  s.setDate(s.getDate() - s.getDay());
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${s.getFullYear()}-${pad(s.getMonth() + 1)}-${pad(s.getDate())}`;
}

function addDaysStr(dateStr: string, days: number): string {
  const d = new Date(`${dateStr}T12:00:00`);
  d.setDate(d.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function TimecardEditor({ employees, onChanged }: { employees: Employee[]; onChanged: () => void }) {
  const [empId, setEmpId] = useState("");
  const [weekStart, setWeekStart] = useState(sundayOf(new Date()));
  const [rows, setRows] = useState<PunchRow[]>([]);
  const [edits, setEdits] = useState<Record<string, { in: string; out: string }>>({});
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [addDate, setAddDate] = useState("");
  const [addIn, setAddIn] = useState("");
  const [addOut, setAddOut] = useState("");

  const weekEnd = addDaysStr(weekStart, 6);

  const loadTimecard = useCallback(async () => {
    if (!empId) { setRows([]); return; }
    try {
      const res = await api.get(`/scheduling/timecard/${empId}?start=${weekStart}&end=${weekEnd}`);
      setRows(res.data);
      setEdits({});
      setMsg("");
    } catch {
      setRows([]);
      setMsg("Could not load timecard");
    }
  }, [empId, weekStart, weekEnd]);

  useEffect(() => { loadTimecard(); }, [loadTimecard]);

  const changed = (r: PunchRow) => {
    const e = edits[r.id];
    if (!e) return false;
    return e.in !== toLocalInputValue(r.punch_in_at) || e.out !== toLocalInputValue(r.punch_out_at);
  };

  const getEdit = (r: PunchRow) =>
    edits[r.id] || { in: toLocalInputValue(r.punch_in_at), out: toLocalInputValue(r.punch_out_at) };

  const setEdit = (r: PunchRow, field: "in" | "out", value: string) => {
    setEdits((prev) => ({ ...prev, [r.id]: { ...getEdit(r), [field]: value } }));
  };

  const savePunch = async (r: PunchRow) => {
    const e = getEdit(r);
    if (!e.in) { setMsg("Punch in time is required"); return; }
    setBusy(true);
    try {
      await api.put(`/scheduling/punches/${r.id}`, {
        punch_in_at: new Date(e.in).toISOString(),
        punch_out_at: e.out ? new Date(e.out).toISOString() : undefined,
        edit_reason: reason || "manager adjustment",
      });
      setMsg("Saved");
      await loadTimecard();
      onChanged();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setMsg(axiosErr.response?.data?.error || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const removePunch = async (r: PunchRow) => {
    if (!window.confirm("Delete this punch? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.delete(`/scheduling/punches/${r.id}`);
      setMsg("Punch deleted");
      await loadTimecard();
      onChanged();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setMsg(axiosErr.response?.data?.error || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const addPunch = async () => {
    if (!empId || !addDate || !addIn) return;
    setBusy(true);
    try {
      await api.post("/scheduling/punches", {
        employee_id: empId,
        punch_in_at: new Date(`${addDate}T${addIn}:00`).toISOString(),
        punch_out_at: addOut ? new Date(`${addDate}T${addOut}:00`).toISOString() : undefined,
        edit_reason: reason || "manager adjustment (missed punch)",
      });
      setAddDate(""); setAddIn(""); setAddOut("");
      setMsg("Punch added");
      await loadTimecard();
      onChanged();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setMsg(axiosErr.response?.data?.error || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  const fmtDay = (iso: string) =>
    new Date(iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

  return (
    <Card className="mt-6">
      <CardHeader title="Timecards — Fix Punches" />
      <p className="text-sm mb-4" style={{ color: "var(--rbw-gray-dark)" }}>
        Adjust clock in/out times, add a missed punch, or delete a bad one.
      </p>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <FormGroup label="Employee">
          <Select value={empId} onChange={(v) => setEmpId(v)}>
            <option value="">Select employee...</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </Select>
        </FormGroup>
        <FormGroup label="Week of (Sunday)">
          <Input type="date" value={weekStart} onChange={(v) => v && setWeekStart(sundayOf(new Date(`${v}T12:00:00`)))} />
        </FormGroup>
        <FormGroup label="Reason for changes">
          <Input type="text" placeholder="e.g. forgot to clock out" value={reason} onChange={(v) => setReason(v)} />
        </FormGroup>
        {msg && (
          <span className="text-sm font-semibold pb-2" style={{ color: msg === "Saved" || msg.endsWith("ed") ? "var(--rbw-success)" : "var(--rbw-danger)" }}>
            {msg}
          </span>
        )}
      </div>

      {empId && (
        <>
          <Table>
            <Thead>
              <Th>Day</Th>
              <Th>Clock In</Th>
              <Th>Clock Out</Th>
              <Th align="center">Hours</Th>
              <Th align="right">Actions</Th>
            </Thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={5}><EmptyState message="No punches this week" /></td></tr>
              ) : (
                rows.map((r, idx) => {
                  const e = getEdit(r);
                  return (
                    <TRow key={r.id} index={idx}>
                      <Td bold>
                        {fmtDay(r.punch_in_at)}
                        {r.edited_by && (
                          <span title={r.edit_reason || "edited"} className="ml-1 text-xs" style={{ color: "var(--rbw-gray-dark)" }}>✎</span>
                        )}
                        {(r.punch_in_note || r.punch_out_note) && (
                          <div className="text-xs font-normal mt-0.5 max-w-[200px]" style={{ color: "#B45309" }}>
                            {r.punch_in_note && <div>In: {r.punch_in_note}</div>}
                            {r.punch_out_note && <div>Out: {r.punch_out_note}</div>}
                          </div>
                        )}
                      </Td>
                      <Td>
                        <input
                          type="datetime-local"
                          value={e.in}
                          onChange={(ev) => setEdit(r, "in", ev.target.value)}
                          className="px-2 py-1 rounded-lg text-sm"
                          style={{ border: "1px solid var(--rbw-border)", background: "var(--rbw-bg-card)", color: "var(--rbw-navy)" }}
                        />
                      </Td>
                      <Td>
                        <input
                          type="datetime-local"
                          value={e.out}
                          onChange={(ev) => setEdit(r, "out", ev.target.value)}
                          className="px-2 py-1 rounded-lg text-sm"
                          style={{ border: "1px solid var(--rbw-border)", background: "var(--rbw-bg-card)", color: "var(--rbw-navy)" }}
                        />
                        {!r.punch_out_at && !e.out && (
                          <span className="ml-2 text-xs font-semibold" style={{ color: "var(--rbw-success)" }}>still in</span>
                        )}
                      </Td>
                      <Td align="center">{r.hours != null ? Number(r.hours).toFixed(2) : "—"}</Td>
                      <Td align="right">
                        <div className="flex justify-end gap-2">
                          {changed(r) && (
                            <Button size="sm" onClick={() => savePunch(r)} disabled={busy}>Save</Button>
                          )}
                          <Button variant="danger" size="sm" onClick={() => removePunch(r)} disabled={busy}>Delete</Button>
                        </div>
                      </Td>
                    </TRow>
                  );
                })
              )}
            </tbody>
          </Table>

          <div className="flex flex-wrap items-end gap-3 mt-4 pt-4" style={{ borderTop: "1px solid var(--rbw-border)" }}>
            <span className="text-sm font-bold pb-2" style={{ color: "var(--rbw-navy)" }}>Add missed punch:</span>
            <FormGroup label="Date">
              <Input type="date" value={addDate} onChange={(v) => setAddDate(v)} />
            </FormGroup>
            <FormGroup label="Clock in">
              <Input type="time" value={addIn} onChange={(v) => setAddIn(v)} />
            </FormGroup>
            <FormGroup label="Clock out (optional)">
              <Input type="time" value={addOut} onChange={(v) => setAddOut(v)} />
            </FormGroup>
            <div className="pb-1">
              <Button onClick={addPunch} disabled={busy || !addDate || !addIn}>Add Punch</Button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}

// ============================================================
// TIME CLOCK TAB
// ============================================================

function TimeClockTab({ employees }: { employees: Employee[] }) {
  const [activeClocks, setActiveClocks] = useState<ClockEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(new Date());
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadActive = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/scheduling/clock/active");
      setActiveClocks(res.data.active || res.data || []);
    } catch {
      setActiveClocks([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadActive();
    timerRef.current = setInterval(() => setNow(new Date()), 30000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [loadActive]);

  const clockIn = async (empId: string) => {
    try {
      await api.post(`/scheduling/clock/in/${empId}`);
      loadActive();
    } catch (err) {
      console.error("Clock in failed:", err);
    }
  };

  const clockOut = async (empId: string) => {
    try {
      await api.post(`/scheduling/clock/out/${empId}`);
      loadActive();
    } catch (err) {
      console.error("Clock out failed:", err);
    }
  };

  const clockedInIds = new Set(activeClocks.map((c) => c.employee_id));
  const activeEmployees = employees.filter((e) => e.status === "active");

  const hoursColor = (hrs: number): string => {
    if (hrs >= 38) return "var(--rbw-danger)";
    if (hrs >= 32) return "#D97706";
    return "var(--rbw-success)";
  };

  return (
    <div>
      {/* Currently Clocked In */}
      {activeClocks.length > 0 && (
        <Card className="mb-6">
          <CardHeader title="Currently Clocked In" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {activeClocks.map((c) => {
              const elapsed = (now.getTime() - new Date(c.punch_in_at).getTime()) / 60000;
              return (
                <div
                  key={c.employee_id}
                  className="rounded-xl p-4 flex items-center justify-between"
                  style={{ background: "#2D8A4E10", border: "1px solid #2D8A4E30" }}
                >
                  <div>
                    <div className="text-sm font-semibold" style={{ color: "var(--rbw-navy)" }}>
                      {c.employee_name}
                    </div>
                    <div className="text-xs" style={{ color: "var(--rbw-success)" }}>
                      {fmtElapsed(elapsed)}
                    </div>
                    {c.punch_in_note && (
                      <div className="text-xs mt-0.5" style={{ color: "#B45309" }} title={c.punch_in_note}>
                        &ldquo;{c.punch_in_note}&rdquo;
                      </div>
                    )}
                  </div>
                  <Button variant="danger" size="sm" onClick={() => clockOut(c.employee_id)}>
                    Out
                  </Button>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Employee List */}
      <Table>
        <Thead>
          <Th>Employee</Th>
          <Th align="center">Hours This Week</Th>
          <Th align="center">Status</Th>
          <Th align="right">Action</Th>
        </Thead>
        <tbody>
          {loading ? (
            <TRow>
              <Td>Loading...</Td>
              <Td>&nbsp;</Td>
              <Td>&nbsp;</Td>
              <Td>&nbsp;</Td>
            </TRow>
          ) : activeEmployees.length === 0 ? (
            <tr>
              <td colSpan={4}>
                <EmptyState message="No employees" />
              </td>
            </tr>
          ) : (
            activeEmployees.map((emp, idx) => {
              const isClockedIn = clockedInIds.has(emp.id);
              const hrs = emp.hours_this_week || 0;
              return (
                <TRow key={emp.id} index={idx}>
                  <Td bold>{emp.name}</Td>
                  <Td align="center">
                    <span className="font-semibold" style={{ color: hoursColor(hrs) }}>
                      {hrs.toFixed(1)}h
                    </span>
                  </Td>
                  <Td align="center">
                    {isClockedIn ? (
                      <Badge variant="green">Clocked In</Badge>
                    ) : (
                      <Badge>Off</Badge>
                    )}
                  </Td>
                  <Td align="right">
                    {isClockedIn ? (
                      <Button variant="danger" size="sm" onClick={() => clockOut(emp.id)}>
                        Clock Out
                      </Button>
                    ) : (
                      <Button variant="secondary" size="sm" onClick={() => clockIn(emp.id)}>
                        Clock In
                      </Button>
                    )}
                  </Td>
                </TRow>
              );
            })
          )}
        </tbody>
      </Table>

      <TimecardEditor employees={activeEmployees} onChanged={loadActive} />
    </div>
  );
}

// ============================================================
// EMPLOYEES TAB
// ============================================================

interface EmployeeDoc {
  id: string;
  name: string;
  mime_type: string;
  created_at: string;
  size_bytes: number;
  uploaded_by_name: string | null;
}

// Documents on an employee's profile: signed handbook, certifications, etc.
function EmployeeDocsModal({ employee, onClose }: { employee: Employee; onClose: () => void }) {
  const [docs, setDocs] = useState<EmployeeDoc[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/scheduling/employees/${employee.id}/documents`);
      setDocs(res.data);
    } catch {
      setMsg("Could not load documents");
    }
  }, [employee.id]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file: File) => {
    setBusy(true);
    setMsg("");
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
        reader.onerror = () => reject(new Error("read failed"));
        reader.readAsDataURL(file);
      });
      await api.post(`/scheduling/employees/${employee.id}/documents`, {
        name: file.name,
        mime_type: file.type || "application/octet-stream",
        data_base64: base64,
      });
      setMsg("Uploaded");
      await load();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setMsg(axiosErr.response?.data?.error || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const view = async (doc: EmployeeDoc) => {
    try {
      const res = await api.get(`/scheduling/documents/${doc.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setMsg("Could not open document");
    }
  };

  const remove = async (doc: EmployeeDoc) => {
    if (!window.confirm(`Delete "${doc.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/scheduling/documents/${doc.id}`);
      await load();
    } catch {
      setMsg("Delete failed");
    }
  };

  return (
    <Modal open onClose={onClose} title={`Documents — ${employee.name}`}>
      {msg && (
        <p className="text-sm font-semibold mb-3" style={{ color: msg === "Uploaded" ? "var(--rbw-success)" : "var(--rbw-danger)" }}>
          {msg}
        </p>
      )}
      {docs.length === 0 ? (
        <p className="text-sm mb-4" style={{ color: "var(--rbw-gray-dark)" }}>
          No documents yet. Upload a signed handbook page, certification, or anything else that belongs on {employee.name.split(" ")[0]}&apos;s file.
        </p>
      ) : (
        <ul className="divide-y mb-4" style={{ borderColor: "var(--rbw-border)" }}>
          {docs.map((d) => (
            <li key={d.id} className="py-2.5 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate" style={{ color: "var(--rbw-navy)" }}>{d.name}</div>
                <div className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>
                  {new Date(d.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  {" · "}{Math.max(1, Math.round(d.size_bytes / 1024))} KB
                  {d.uploaded_by_name ? ` · by ${d.uploaded_by_name}` : ""}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button variant="ghost" size="sm" onClick={() => view(d)}>View</Button>
                <Button variant="danger" size="sm" onClick={() => remove(d)}>Delete</Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <label
        className="block w-full text-center py-3 rounded-xl font-bold text-sm cursor-pointer"
        style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)", opacity: busy ? 0.5 : 1 }}
      >
        {busy ? "Uploading..." : "+ Upload Document"}
        <input
          type="file"
          className="hidden"
          disabled={busy}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) upload(f);
            e.target.value = "";
          }}
        />
      </label>
    </Modal>
  );
}

function EmployeesTab({
  employees,
  onRefresh,
}: {
  employees: Employee[];
  onRefresh: () => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [editEmp, setEditEmp] = useState<Employee | null>(null);
  const [docsEmp, setDocsEmp] = useState<Employee | null>(null);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [rate, setRate] = useState("");
  const [payType, setPayType] = useState<"W2" | "1099">("W2");
  const [hireDate, setHireDate] = useState("");
  const [status, setStatus] = useState<"active" | "inactive">("active");

  const resetForm = () => {
    setName("");
    setPhone("");
    setRate("");
    setPayType("W2");
    setHireDate("");
    setStatus("active");
    setEditEmp(null);
  };

  const openEdit = (e: Employee) => {
    setEditEmp(e);
    setName(e.name);
    setPhone(e.phone || "");
    setRate(String(e.hourly_rate || ""));
    setPayType(e.pay_type || "W2");
    setHireDate(e.hire_date ? e.hire_date.split("T")[0] : "");
    setStatus(e.status);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!name) return;
    // API stores first/last name and is_active, not name/status.
    const [firstName, ...restName] = name.trim().split(/\s+/);
    const payload = {
      first_name: firstName,
      last_name: restName.join(" "),
      phone,
      hourly_rate: parseFloat(rate) || 0,
      pay_type: payType,
      hire_date: hireDate || undefined,
      is_active: status === "active",
    };
    try {
      if (editEmp) {
        await api.put(`/scheduling/employees/${editEmp.id}`, payload);
      } else {
        await api.post("/scheduling/employees", payload);
      }
      setShowForm(false);
      resetForm();
      onRefresh();
    } catch (err) {
      console.error("Failed to save employee:", err);
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button
          size="sm"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          + Add Employee
        </Button>
      </div>

      <Table>
        <Thead>
          <Th>Name</Th>
          <Th>Phone</Th>
          <Th align="right">Rate</Th>
          <Th align="center">Pay Type</Th>
          <Th>Hire Date</Th>
          <Th align="center">Hours/Week</Th>
          <Th align="center">Status</Th>
          <Th align="right">Action</Th>
        </Thead>
        <tbody>
          {employees.length === 0 ? (
            <tr>
              <td colSpan={8}>
                <EmptyState message="No employees yet" detail="Click Add Employee to get started" />
              </td>
            </tr>
          ) : (
            employees.map((emp, idx) => (
              <TRow key={emp.id} index={idx}>
                <Td bold>{emp.name}</Td>
                <Td>{emp.phone || "-"}</Td>
                <Td align="right">{emp.hourly_rate ? fmtMoney(emp.hourly_rate) : "-"}</Td>
                <Td align="center">
                  <Badge variant={emp.pay_type === "W2" ? "blue" : "purple"}>{emp.pay_type}</Badge>
                </Td>
                <Td>
                  {emp.hire_date
                    ? new Date(emp.hire_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                    : "-"}
                </Td>
                <Td align="center">{(emp.hours_this_week || 0).toFixed(1)}</Td>
                <Td align="center">
                  <Badge variant={emp.status === "active" ? "green" : "default"}>{emp.status}</Badge>
                </Td>
                <Td align="right">
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setDocsEmp(emp)}>
                      Docs
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(emp)}>
                      Edit
                    </Button>
                  </div>
                </Td>
              </TRow>
            ))
          )}
        </tbody>
      </Table>

      {docsEmp && <EmployeeDocsModal employee={docsEmp} onClose={() => setDocsEmp(null)} />}

      <Modal
        open={showForm}
        onClose={() => {
          setShowForm(false);
          resetForm();
        }}
        title={editEmp ? "Edit Employee" : "Add Employee"}
      >
        <div className="grid grid-cols-2 gap-4">
          <FormGroup label="Full Name" span={2}>
            <Input value={name} onChange={setName} placeholder="Jane Smith" required />
          </FormGroup>
          <FormGroup label="Phone">
            <Input value={phone} onChange={setPhone} placeholder="717-555-0123" />
          </FormGroup>
          <FormGroup label="Hourly Rate">
            <Input type="number" value={rate} onChange={setRate} step="0.25" min="0" placeholder="15.00" />
          </FormGroup>
          <FormGroup label="Pay Type">
            <Select value={payType} onChange={(v) => setPayType(v as "W2" | "1099")}>
              <option value="W2">W2</option>
              <option value="1099">1099</option>
            </Select>
          </FormGroup>
          <FormGroup label="Hire Date">
            <Input type="date" value={hireDate} onChange={setHireDate} />
          </FormGroup>
          {editEmp && (
            <FormGroup label="Status">
              <Select value={status} onChange={(v) => setStatus(v as "active" | "inactive")}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Select>
            </FormGroup>
          )}
        </div>
        <div className="flex gap-3 mt-6">
          <Button onClick={handleSave}>{editEmp ? "Save Changes" : "Add Employee"}</Button>
          <Button
            variant="ghost"
            onClick={() => {
              setShowForm(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
        </div>
      </Modal>
    </div>
  );
}

// ============================================================
// TIME OFF TAB
// ============================================================

function TimeOffTab({ employees }: { employees: Employee[] }) {
  const { user } = useAuth();
  const isManager = user?.role === "owner" || user?.role === "manager";
  const [requests, setRequests] = useState<TimeOffRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [formEmpId, setFormEmpId] = useState("");
  const [formStart, setFormStart] = useState("");
  const [formEnd, setFormEnd] = useState("");
  const [formReason, setFormReason] = useState("");

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/scheduling/time-off");
      setRequests(res.data.requests || res.data || []);
    } catch {
      setRequests([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleDecide = async (id: string, decision: "approved" | "denied") => {
    try {
      await api.post(`/scheduling/time-off/${id}/decide`, { decision });
      loadRequests();
    } catch (err) {
      console.error("Failed to decide:", err);
    }
  };

  const handleSubmit = async () => {
    if (!formEmpId || !formStart || !formEnd) return;
    try {
      await api.post("/scheduling/time-off", {
        employee_id: formEmpId,
        start_date: formStart,
        end_date: formEnd,
        reason: formReason,
      });
      setShowForm(false);
      setFormEmpId("");
      setFormStart("");
      setFormEnd("");
      setFormReason("");
      loadRequests();
    } catch (err) {
      console.error("Failed to submit:", err);
    }
  };

  const pending = requests.filter((r) => r.status === "pending");
  const decided = requests.filter((r) => r.status !== "pending");
  const activeEmployees = employees.filter((e) => e.status === "active");

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button size="sm" onClick={() => setShowForm(true)}>
          + Request Time Off
        </Button>
      </div>

      {/* Pending */}
      {pending.length > 0 && (
        <Card className="mb-6">
          <CardHeader title="Pending Requests" />
          <div className="space-y-3">
            {pending.map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between rounded-xl p-4"
                style={{ background: "var(--rbw-bg)", border: "1px solid var(--rbw-border)" }}
              >
                <div>
                  <div className="text-sm font-semibold" style={{ color: "var(--rbw-navy)" }}>
                    {r.employee_name}
                  </div>
                  <div className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>
                    {new Date(r.start_date).toLocaleDateString()} &ndash; {new Date(r.end_date).toLocaleDateString()}
                  </div>
                  {r.reason && (
                    <div className="text-xs mt-1" style={{ color: "var(--rbw-gray-dark)" }}>
                      {r.reason}
                    </div>
                  )}
                </div>
                {isManager && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => handleDecide(r.id, "approved")}>
                      Approve
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => handleDecide(r.id, "denied")}>
                      Deny
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {pending.length === 0 && (
        <Card className="mb-6">
          <EmptyState message="No pending requests" />
        </Card>
      )}

      {/* History */}
      {decided.length > 0 && (
        <Table>
          <Thead>
            <Th>Employee</Th>
            <Th>Dates</Th>
            <Th>Reason</Th>
            <Th align="center">Status</Th>
          </Thead>
          <tbody>
            {decided.map((r, idx) => (
              <TRow key={r.id} index={idx}>
                <Td bold>{r.employee_name}</Td>
                <Td>
                  {new Date(r.start_date).toLocaleDateString()} &ndash; {new Date(r.end_date).toLocaleDateString()}
                </Td>
                <Td>{r.reason || "-"}</Td>
                <Td align="center">
                  <Badge variant={r.status === "approved" ? "green" : "danger"}>{r.status}</Badge>
                </Td>
              </TRow>
            ))}
          </tbody>
        </Table>
      )}

      {/* Request Form Modal */}
      <Modal open={showForm} onClose={() => setShowForm(false)} title="Request Time Off">
        <div className="grid grid-cols-2 gap-4">
          <FormGroup label="Employee" span={2}>
            <Select value={formEmpId} onChange={setFormEmpId}>
              <option value="">Select employee...</option>
              {activeEmployees.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup label="Start Date">
            <Input type="date" value={formStart} onChange={setFormStart} />
          </FormGroup>
          <FormGroup label="End Date">
            <Input type="date" value={formEnd} onChange={setFormEnd} />
          </FormGroup>
          <FormGroup label="Reason" span={2}>
            <Input value={formReason} onChange={setFormReason} placeholder="Vacation, sick, etc." />
          </FormGroup>
        </div>
        <div className="flex gap-3 mt-6">
          <Button onClick={handleSubmit}>Submit Request</Button>
          <Button variant="ghost" onClick={() => setShowForm(false)}>
            Cancel
          </Button>
        </div>
      </Modal>
    </div>
  );
}

// ============================================================
// PAYROLL TAB
// ============================================================

function PayrollTab() {
  // Bottle shop payroll weeks run Sunday through Saturday.
  // Default to the last completed Sun-Sat week.
  const [periodStart, setPeriodStart] = useState(() => {
    const lastSat = new Date();
    lastSat.setDate(lastSat.getDate() - lastSat.getDay() - 1);
    const sun = addDays(lastSat, -6);
    return fmtDate(sun);
  });
  const [periodEnd, setPeriodEnd] = useState(() => {
    const lastSat = new Date();
    lastSat.setDate(lastSat.getDate() - lastSat.getDay() - 1);
    return fmtDate(lastSat);
  });
  const [calc, setCalc] = useState<PayrollCalc | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState<PayrollHistory[]>([]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.get("/scheduling/payroll/history");
      setHistory(res.data.history || res.data || []);
    } catch {
      setHistory([]);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleCalc = async () => {
    setLoading(true);
    try {
      const res = await api.post("/scheduling/payroll/calculate", {
        period_start: periodStart,
        period_end: periodEnd,
      });
      setCalc(res.data);
    } catch (err) {
      console.error("Payroll calc failed:", err);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post("/scheduling/payroll/save");
      loadHistory();
      setCalc(null);
    } catch (err) {
      console.error("Payroll save failed:", err);
    }
    setSaving(false);
  };

  const totalReg = calc?.rows.reduce((s, r) => s + r.regular_pay, 0) || 0;
  const totalOT = calc?.rows.reduce((s, r) => s + r.ot_pay, 0) || 0;
  const totalGross = calc?.rows.reduce((s, r) => s + r.gross_pay, 0) || 0;

  return (
    <div>
      {/* Date Range */}
      <Card className="mb-6">
        <div className="flex items-end gap-4 flex-wrap">
          <FormGroup label="Period Start">
            <Input type="date" value={periodStart} onChange={setPeriodStart} />
          </FormGroup>
          <FormGroup label="Period End">
            <Input type="date" value={periodEnd} onChange={setPeriodEnd} />
          </FormGroup>
          <Button onClick={handleCalc} disabled={loading}>
            {loading ? "Calculating..." : "Calculate Payroll"}
          </Button>
        </div>
      </Card>

      {/* Results */}
      {calc && (
        <>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <StatCard label="Regular Pay" value={fmtMoney(totalReg)} variant="blue" />
            <StatCard label="Overtime Pay" value={fmtMoney(totalOT)} variant="gold" />
            <StatCard label="Total Gross" value={fmtMoney(totalGross)} variant="green" />
          </div>

          <Table>
            <Thead>
              <Th>Employee</Th>
              <Th align="right">Reg Hours</Th>
              <Th align="right">OT Hours</Th>
              <Th align="right">Rate</Th>
              <Th align="right">Reg Pay</Th>
              <Th align="right">OT Pay</Th>
              <Th align="right">Gross Pay</Th>
            </Thead>
            <tbody>
              {calc.rows.map((r, idx) => (
                <TRow key={r.employee_id} index={idx}>
                  <Td bold>{r.employee_name}</Td>
                  <Td align="right">{r.regular_hours.toFixed(2)}</Td>
                  <Td align="right">{r.ot_hours > 0 ? r.ot_hours.toFixed(2) : "-"}</Td>
                  <Td align="right">{fmtMoney(r.rate)}</Td>
                  <Td align="right">{fmtMoney(r.regular_pay)}</Td>
                  <Td align="right">{r.ot_pay > 0 ? fmtMoney(r.ot_pay) : "-"}</Td>
                  <Td align="right">{fmtMoney(r.gross_pay)}</Td>
                </TRow>
              ))}
              {/* Total row */}
              <tr style={{ background: "var(--rbw-bg)", borderTop: "2px solid var(--rbw-navy)" }}>
                <td className="px-5 py-4 text-sm font-bold" style={{ color: "var(--rbw-navy)" }}>
                  TOTAL
                </td>
                <td className="px-5 py-4 text-sm font-bold text-right" style={{ color: "var(--rbw-navy)" }}>
                  {calc.rows.reduce((s, r) => s + r.regular_hours, 0).toFixed(2)}
                </td>
                <td className="px-5 py-4 text-sm font-bold text-right" style={{ color: "var(--rbw-navy)" }}>
                  {calc.rows.reduce((s, r) => s + r.ot_hours, 0).toFixed(2)}
                </td>
                <td>&nbsp;</td>
                <td className="px-5 py-4 text-sm font-bold text-right" style={{ color: "var(--rbw-navy)" }}>
                  {fmtMoney(totalReg)}
                </td>
                <td className="px-5 py-4 text-sm font-bold text-right" style={{ color: "var(--rbw-navy)" }}>
                  {fmtMoney(totalOT)}
                </td>
                <td className="px-5 py-4 text-sm font-bold text-right" style={{ color: "var(--rbw-navy)" }}>
                  {fmtMoney(totalGross)}
                </td>
              </tr>
            </tbody>
          </Table>

          <div className="flex justify-end mt-4">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save & Process"}
            </Button>
          </div>
        </>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-bold mb-4" style={{ color: "var(--rbw-navy)" }}>
            Payroll History
          </h3>
          <Table>
            <Thead>
              <Th>Period</Th>
              <Th align="right">Total Gross</Th>
              <Th>Processed</Th>
            </Thead>
            <tbody>
              {history.map((h, idx) => (
                <TRow key={h.id} index={idx}>
                  <Td>
                    {new Date(h.period_start).toLocaleDateString()} &ndash;{" "}
                    {new Date(h.period_end).toLocaleDateString()}
                  </Td>
                  <Td align="right">{fmtMoney(h.total_gross)}</Td>
                  <Td>
                    {new Date(h.created_at).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </Td>
                </TRow>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// MAIN PAGE
// ============================================================

// Payroll lives on its own page (/payroll) — one place, cash + CPA together.
const TABS = [
  { key: "schedule", label: "Schedule" },
  { key: "clock", label: "Time Clock" },
  { key: "employees", label: "Employees" },
  { key: "timeoff", label: "Time Off" },
];

export default function SchedulingPage() {
  const { user } = useAuth();
  const isManager = user?.role === "owner" || user?.role === "manager";
  const visibleTabs = isManager ? TABS : TABS.filter((t) => t.key === "schedule");
  const [activeTab, setActiveTab] = useState("schedule");
  const [employees, setEmployees] = useState<Employee[]>([]);

  const loadEmployees = useCallback(async () => {
    try {
      const res = await api.get("/scheduling/employees");
      const rows = res.data.employees || res.data || [];
      // Adapt API shape (first_name/last_name, is_active) to this page's model.
      setEmployees(rows.map((e: {
        id: string; first_name: string; last_name: string; phone: string | null;
        hourly_rate: string | number | null; pay_type: string; hire_date: string | null;
        is_active: boolean; hours_this_week: string | number | null;
      }) => ({
        id: e.id,
        name: `${e.first_name} ${e.last_name}`.trim(),
        phone: e.phone || "",
        hourly_rate: Number(e.hourly_rate) || 0,
        pay_type: e.pay_type === "W2" ? "W2" : "1099",
        hire_date: e.hire_date || "",
        status: e.is_active ? "active" : "inactive",
        hours_this_week: Number(e.hours_this_week) || 0,
      })));
    } catch {
      setEmployees([]);
    }
  }, []);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  return (
    <PageContainer wide>
      <PageHeader title={isManager ? "Scheduling & Payroll" : "Store Schedule"} subtitle={isManager ? "Schedule, time clock, employees, and payroll" : "Who works when this week"} />
      <TabGroup tabs={visibleTabs} active={activeTab} onChange={setActiveTab} />

      {activeTab === "schedule" && <ScheduleTab employees={employees} />}
      {activeTab === "clock" && <TimeClockTab employees={employees} />}
      {activeTab === "employees" && <EmployeesTab employees={employees} onRefresh={loadEmployees} />}
      {activeTab === "timeoff" && <TimeOffTab employees={employees} />}
    </PageContainer>
  );
}
